import styled from 'styled-components';
import { styledTable } from '../styled/mixin';
import {
  AssetType,
  Expense,
  expenseStatusKorean,
} from '../../modules/expenses';
import { Button, Col, Popconfirm, Row, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { getExpenseType, priceToString } from '../../util/utils';
import Title from 'antd/lib/typography/Title';
import { DownloadOutlined, SyncOutlined } from '@ant-design/icons';

const StyledDiv = styled.div`
  ${styledTable}
`;

interface ExpenseListComponentProps {
  data: Expense[] | null;
  syncCardApprovalLoading: boolean;
  syncTaxInvoiceLoading: boolean;
  onDelete: (expenseId: string) => void;
  moveToDetailPage: (expenseId: string) => void;
  downloadFile: (data: any) => void;
  syncCardApprovalList: () => void;
  syncTaxInvoiceList: () => void;
}

function ExpenseListComponent({
  data,
  syncCardApprovalLoading,
  syncTaxInvoiceLoading,
  moveToDetailPage,
  onDelete,
  downloadFile,
  syncCardApprovalList,
  syncTaxInvoiceList,
}: ExpenseListComponentProps) {
  const columns: ColumnsType<Expense> = [
    {
      title: '회사명',
      dataIndex: 'companyName',
      ellipsis: true,
      sorter: (a: Expense, b: Expense) =>
        a.companyName.localeCompare(b.companyName),
      width: '13%',
      render: (text: any, record: any) => (
        <>
          <span
            className="clickable-table-item"
            onClick={() => moveToDetailPage(record.id)}
          >
            {text}
          </span>
        </>
      ),
    },
    {
      title: '종류',
      dataIndex: ['financialAssetInfo', 'assetType'],
      width: '8%',
      sorter: (a: Expense, b: Expense) =>
        getExpenseType(a)?.localeCompare(getExpenseType(b)),
      render: (text: any, record: any) => <>{getExpenseType(record)}</>,
    },
    {
      title: '용도',
      dataIndex: 'category',
      ellipsis: true,
      sorter: (a: Expense, b: Expense) =>
        a.category === null || b.category === null
          ? -1
          : a.category.localeCompare(b.category),
      width: '7%',
    },
    {
      title: '공종',
      dataIndex: 'gongjong',
      ellipsis: true,
      render: (text: any, record: any) => <>{text === null ? '기타' : text}</>,
      sorter: (a: Expense, b: Expense) =>
        a.gongjong === null || b.gongjong === null
          ? -1
          : a.gongjong.localeCompare(b.gongjong),
      width: '7%',
    },
    // {
    //   title: '경비환급',
    //   dataIndex: 'isRefund',
    //   ellipsis: true,
    //   width: '7%',
    //   render: (value: any, record: any, index: any) =>
    //     value ? '예' : '아니오',
    // },
    {
      title: '사용일시',
      dataIndex: 'usedDateTime',
      defaultSortOrder: 'descend',
      width: '11%',
      sorter: (a: Expense, b: Expense) =>
        a.usedDateTime === null || b.usedDateTime === null
          ? -1
          : a.usedDateTime.localeCompare(b.usedDateTime),
      render: (text: any, record: any, index: any) =>
        record.usedDateTime === null
          ? ''
          : record.financialAssetInfo?.assetType === AssetType.CARD
          ? moment(record.usedDateTime).format('YYYY-MM-DD HH:mm')
          : moment(record.usedDateTime).format('YYYY-MM-DD'),
    },
    {
      title: '공사명',
      dataIndex: ['project', 'name'],
      ellipsis: true,
      sorter: (a: Expense, b: Expense) =>
        a?.project?.name.localeCompare(b?.project?.name),
      width: '12%',
      render: (text: any, record: any) => <>{text === null ? '' : text}</>,
    },
    {
      title: '제출자',
      dataIndex: ['usedBy', 'name'],
      // sorter: (a: Expense, b: Expense) =>
      //   a.usedBy.name.localeCompare(b.usedBy.name),
      width: '6%',
      render: (text: any, record: any) => <>{text === null ? '' : text}</>,
    },
    {
      title: '상태',
      dataIndex: 'expenseStatus',
      width: '5%',
      render: (text: any, record: Expense) =>
        expenseStatusKorean[record.expenseStatus],
    },
    // {
    //   title: '설명',
    //   dataIndex: 'description',
    //   width: '10%',
    // },
    {
      title: '공급가액(원)',
      width: '11%',
      align: 'right',
      render: (value: any, record: any, index: any) =>
        priceToString(record.price - record.vat),
    },
    {
      title: '부가세(원)',
      dataIndex: 'vat',
      width: '8%',
      align: 'right',
      render: (value: any, record: any, index: any) => priceToString(value),
    },
    {
      title: '합계(원)',
      dataIndex: 'price',
      sorter: (a: Expense, b: Expense) => a.price - b.price,
      width: '11%',
      align: 'right',
      render: (value: any, record: any, index: any) => priceToString(value),
    },
    {
      title: '',
      key: 'action',
      dataIndex: '',
      align: 'center',
      width: '5%',
      render: (text, record, index) => (
        <Space size="small">
          {/* <div onClick={(e) => e.stopPropagation()}>
            <Button size="small" onClick={() => onEdit(record.id + '')}>
              수정
            </Button>
          </div> */}
          <div onClick={(e) => e.stopPropagation()}>
            <Popconfirm
              title="정말 삭제하시겠습니까?"
              data-test={`project-list-item-${index}`}
              onConfirm={() => onDelete(record.id + '')}
            >
              <Button size="small">삭제</Button>
            </Popconfirm>
          </div>
        </Space>
      ),
    },
  ];

  return (
    <StyledDiv>
      <Row justify="space-between">
        <Col>
          <Title level={4}>지출</Title>
        </Col>
        <Col>
          <Space>
            <Button
              size="small"
              icon={<SyncOutlined rev={undefined} />}
              loading={syncTaxInvoiceLoading}
              onClick={() => syncTaxInvoiceList()}
            >
              전자세금계산서
            </Button>
            <Button
              size="small"
              icon={<SyncOutlined rev={undefined} />}
              loading={syncCardApprovalLoading}
              onClick={() => syncCardApprovalList()}
            >
              카드승인내역
            </Button>
            <Button
              size="small"
              icon={<DownloadOutlined rev={undefined} />}
              onClick={() => downloadFile(data)}
            >
              다운로드
            </Button>
          </Space>
        </Col>
      </Row>

      {data && (
        <Table<Expense>
          rowKey="id"
          columns={columns}
          dataSource={data}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100', '300'],
            position: ['bottomCenter'],
          }}
          locale={{
            triggerDesc: '내림차순으로 정렬하려면 클릭하세요.',
            triggerAsc: '오름차순으로 정렬하려면 클릭하세요.',
            cancelSort: '정렬을 취소하려면 클릭하세요.',
          }}
          summary={(pageData) => {
            let totalPrice = 0;
            let totalVat = 0;

            pageData.forEach(({ price, vat }) => {
              totalPrice += price;
              totalVat += vat;
            });

            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  <Table.Summary.Cell index={4}></Table.Summary.Cell>
                  <Table.Summary.Cell index={5}></Table.Summary.Cell>
                  <Table.Summary.Cell index={6}></Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">
                    Total:
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    {priceToString(totalPrice - totalVat)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} align="right">
                    {priceToString(totalVat)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11} align="right">
                    {priceToString(totalPrice)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={12}></Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
        />
      )}
    </StyledDiv>
  );
}

export default ExpenseListComponent;
